import { useState } from "react";
import { IconDownload, IconFileTypePdf, IconPaperclip } from "@tabler/icons-react";
import { Card, Group, Stack, Text, ThemeIcon } from "@/components/ui";
import type { SolicitudDetalle } from "@/lib/types";
import { documentosApi } from "@/features/documentos/services/documentos.api";
import { labelCategoria } from "@/features/documentos/utils/documento-helpers";
import { useDocumentosSolicitud } from "@/features/documentos/hooks/use-documentos-solicitud";
import { descargarInvoicePdf } from "../services/invoice-pdf";
import { toastErrorApi } from "@/lib/toast";

/**
 * Sección read-only de "Documentos" para el resumen del detalle. Lista los
 * documentos de la solicitud (Gestión Documental · fuente única) y permite
 * DESCARGAR cada uno. Para el subtipo Invoice, además muestra arriba el PDF de la
 * FACTURA con su propio endpoint (por eso su categoría se omite de la lista). Sin
 * carga/eliminación — esas acciones viven en el paso del wizard, exclusivo del
 * dueño. Pensado para que roles de consulta y autorizadores revisen el soporte.
 */
export function DocumentosResumenCard({ sol }: { sol: SolicitudDetalle }) {
  const { data: documentos = [] } = useDocumentosSolicitud(sol.id);
  const docs = documentos.filter((d) => d.categoria !== "FacturaInvoice");
  const invoice = sol.invoice ?? null;
  const [descargandoId, setDescargandoId] = useState<string | null>(null);
  if (docs.length === 0 && !invoice) return null;

  const descargar = async (documentoId: string, nombre: string) => {
    setDescargandoId(documentoId);
    try {
      await documentosApi.descargar(sol.id, documentoId, nombre);
    } catch (e) {
      toastErrorApi(e);
    } finally {
      setDescargandoId(null);
    }
  };

  const descargarInvoice = async () => {
    setDescargandoId("invoice");
    try {
      await descargarInvoicePdf(sol.id);
    } catch (e) {
      toastErrorApi(e);
    } finally {
      setDescargandoId(null);
    }
  };

  const totalArchivos = docs.length + (invoice ? 1 : 0);

  return (
    <Card padding="md" id="sec-documentos">
      <Group gap="sm" wrap="nowrap">
        <ThemeIcon variant="filled" color="slate" size="md" radius="md">
          <IconPaperclip size={16} stroke={1.6} />
        </ThemeIcon>
        <Stack gap={0} className="min-w-0">
          <Text fw={600} size="sm">Documentos</Text>
          <Text size="xs" c="dimmed">
            {totalArchivos} archivo{totalArchivos === 1 ? "" : "s"} de soporte
          </Text>
        </Stack>
      </Group>

      <Stack gap={4} className="mt-3">
        {/* Subtipo Invoice · la FACTURA (PDF) va primero y resaltada. */}
        {invoice ? (
          <Group
            justify="space-between"
            wrap="nowrap"
            className="rounded-md border border-multiva-300 bg-multiva-50 px-2.5 py-1.5 dark:border-multiva-800 dark:bg-multiva-900/20"
          >
            <Group gap={8} wrap="nowrap" className="min-w-0">
              <IconFileTypePdf size={18} className="shrink-0 text-peligro-600" />
              <Stack gap={0} className="min-w-0">
                <Text size="sm" fw={600} truncate>
                  Factura (Invoice)
                  {invoice.numeroFactura ? ` · ${invoice.numeroFactura}` : ""}
                </Text>
                <Text size="xs" c="dimmed" truncate>
                  PDF de la factura extranjera · {invoice.emisorNombre}
                </Text>
              </Stack>
            </Group>
            <button
              type="button"
              onClick={() => void descargarInvoice()}
              disabled={descargandoId === "invoice"}
              aria-label="Descargar PDF de la factura (Invoice)"
              className="flex size-8 shrink-0 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground disabled:opacity-50"
            >
              <IconDownload size={16} stroke={1.8} />
            </button>
          </Group>
        ) : null}

        {docs.map((d) => {
          const detalle = d.tipoDocumento ? `${labelCategoria(d.categoria)} · ${d.tipoDocumento}` : labelCategoria(d.categoria);
          return (
            <Group
              key={d.id}
              justify="space-between"
              wrap="nowrap"
              className="rounded-md border border-border px-2.5 py-1.5"
            >
              <Stack gap={0} className="min-w-0">
                <Text size="sm" fw={500} truncate title={d.nombreOriginal}>
                  {d.nombreOriginal}
                </Text>
                <Text size="xs" c="dimmed" truncate>{detalle}</Text>
              </Stack>
              <button
                type="button"
                onClick={() => void descargar(d.documentoId, d.nombreOriginal)}
                disabled={descargandoId === d.documentoId}
                aria-label={`Descargar ${d.nombreOriginal}`}
                className="flex size-8 shrink-0 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground disabled:opacity-50"
              >
                <IconDownload size={16} stroke={1.8} />
              </button>
            </Group>
          );
        })}
      </Stack>
    </Card>
  );
}
